import * as Yup from "yup";
import { VALIDATION_ENUM } from "enums";

// ─── Wspólne reguły walidacji ────────────────────────────────────────────────

const requiredString = Yup.string().required(VALIDATION_ENUM.REQUIRED);

const requiredMin3NoEdgeSpaces = Yup.string()
  .required(VALIDATION_ENUM.REQUIRED)
  .min(3, VALIDATION_ENUM.MIN3)
  .max(100, VALIDATION_ENUM.MAX_100)
  .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE);

const optionalMin1Max100NoEdgeSpaces = Yup.string()
  .notRequired()
  .test("optional-string-format", "", function (val) {
    if (!val) return true;
    if (val.length < 1) return this.createError({ message: VALIDATION_ENUM.MIN1 });
    if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
    if (/^\s/.test(val) || /\s$/.test(val))
      return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
    return true;
  });

const optionalMin3Max100NoEdgeSpaces = Yup.string()
  .notRequired()
  .test("optional-string-format-min3", "", function (val) {
    if (!val) return true;
    if (val.length < 3) return this.createError({ message: VALIDATION_ENUM.MIN3 });
    if (val.length > 100) return this.createError({ message: VALIDATION_ENUM.MAX_100 });
    if (/^\s/.test(val) || /\s$/.test(val))
      return this.createError({ message: VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE });
    return true;
  });

const requiredPositiveNumber = Yup.string()
  .required(VALIDATION_ENUM.REQUIRED)
  .test("is-positive", VALIDATION_ENUM.MIN_1, (val) => {
    if (!val) return false;
    return Number(val) > 0;
  });

const optionalPositiveNumber = Yup.string()
  .notRequired()
  .test("is-positive-if-filled", VALIDATION_ENUM.MIN_1, (val) => {
    if (!val) return true;
    return Number(val) > 0;
  });

const optionalString = Yup.string().notRequired();

const requiredNonNegativeNumberMax999 = Yup.string()
  .required(VALIDATION_ENUM.REQUIRED)
  .test("is-non-negative", VALIDATION_ENUM.MIN_1, (val) => {
    if (val === null || val === undefined || val === "") return false;
    return !isNaN(Number(val)) && Number(val) >= 0;
  })
  .test("is-max-999.99", "Wartość nie może być większa niż 999.99", (val) => {
    if (!val) return true;
    return Number(val) <= 999.99;
  });


const requiredMinPointOne = Yup.string()
  .required(VALIDATION_ENUM.REQUIRED)
  .test("is-min-0.01", "Minimalna wartość to 0.01", (val) => {
    if (!val) return false;
    return Number(val) >= 0.01;
  });

const baseSchema = {
  primaryValue: requiredPositiveNumber,
  additionalInfo: optionalString,
  relianceCanView: Yup.boolean(),
  status: optionalString,
};


const stocksSchema = Yup.object().shape({
  ...baseSchema,
  quantity: requiredPositiveNumber,
  purchaseRate: requiredPositiveNumber,
});

const sharesSchema = Yup.object().shape({
  ...baseSchema,
  quantity: requiredPositiveNumber,
  purchaseRate: requiredPositiveNumber,
});

const realEstateSchema = Yup.object().shape({
  ...baseSchema,
  propertyType: requiredString,
  city: requiredMin3NoEdgeSpaces,
  address: requiredMin3NoEdgeSpaces,
  area: optionalPositiveNumber,
  currencyUuid: requiredString,
  propertyTypeOther: Yup.string().when("propertyType", {
    is: "other",
    then: (schema) =>
      schema
        .required(VALIDATION_ENUM.REQUIRED)
        .min(3, VALIDATION_ENUM.MIN3)
        .max(100, VALIDATION_ENUM.MAX_100)
        .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const cryptoSchema = Yup.object().shape({
  ...baseSchema,
  primaryValue: Yup.string()
    .notRequired()
    .test("is-min-0.01-if-filled", "Minimalna wartość to 0.01", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) >= 0.01;
    }),
  quantity: requiredMinPointOne,
  name: requiredMin3NoEdgeSpaces,
  purchaseDate: requiredString,
});

const preciousStonesSchema = Yup.object().shape({
  ...baseSchema,
  currencyUuid: requiredString,
  weight: Yup.string()
    .notRequired()
    .test("weight-min-0.1", "Minimalna wartość to 0.1", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) >= 0.1;
    }),
  cut: optionalMin1Max100NoEdgeSpaces,
  color: optionalMin1Max100NoEdgeSpaces,
  clarity: optionalMin1Max100NoEdgeSpaces,
  name: requiredMin3NoEdgeSpaces,
});

const optionalfineness = Yup.string()
  .notRequired()
  .test("fineness-format", "Nieprawidłowy format próby", (val) => {
    if (!val) return true;
    if (val.endsWith("%")) return /^\d+(\.\d{1})?%$/.test(val);
    return /^\d+$/.test(val);
  });

const preciousMetalsSchema = Yup.object().shape({
  ...baseSchema,
  currencyUuid: requiredString,
  propertyType: requiredString,
  weight: Yup.string()
    .notRequired()
    .test("weight-min-0.1", "Minimalna wartość to 0.1", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) >= 0.1;
    }),
  fineness: optionalfineness,
  name: Yup.string().when("propertyType", {
    is: "other",
    then: (schema) =>
      schema
        .required(VALIDATION_ENUM.REQUIRED)
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const treasuryBondsSchema = Yup.object().shape({
  ...baseSchema,
  currencyUuid: requiredString,
  quantity: Yup.string()
    .required(VALIDATION_ENUM.REQUIRED)
    .test("is-integer-min-1", "Minimalna wartość to 1, tylko liczby całkowite", (val) => {
      if (!val) return false;
      const num = Number(val);
      return Number.isInteger(num) && num >= 1;
    }),
  interestRate: Yup.string()
    .required(VALIDATION_ENUM.REQUIRED)
    .test("is-min-0.01", "Minimalna wartość to 0.01", (val) => {
      if (!val) return false;
      return Number(val) >= 0.01;
    })
    .test("is-max-100", "Maksymalna wartość to 100", (val) => {
      if (!val) return true;
      return Number(val) <= 100;
    }),
});

const artworkSchema = Yup.object().shape({
  ...baseSchema,
  artworkType: requiredMin3NoEdgeSpaces,
  currencyUuid: requiredString,
  author: Yup.string().when("unknownAuthor", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .required(VALIDATION_ENUM.REQUIRED)
        .min(3, VALIDATION_ENUM.MIN3)
        .max(100, VALIDATION_ENUM.MAX_100)
        .matches(/^[^\s].*?[^\s]$/, VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE),
  }),
  name: optionalMin3Max100NoEdgeSpaces,
  eraAge: optionalMin3Max100NoEdgeSpaces,
});

const investmentPolicySchema = Yup.object().shape({
  ...baseSchema,
  name: requiredMin3NoEdgeSpaces,
  currencyUuid: requiredString,
  primaryValue: optionalfineness,
  durationDateValue: Yup.string().when("durationDate", {
    is: "specific_date",
    then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const loansSchema = Yup.object().shape({
  ...baseSchema,
  entity: requiredMin3NoEdgeSpaces,
  currencyUuid: requiredString,
  interestRate: requiredNonNegativeNumberMax999,
  durationDateValue: Yup.string().when("durationDate", {
    is: "specific_date",
    then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const currenciesSchema = Yup.object().shape({
  ...baseSchema,
  currencyUuid: requiredString,
  purchaseDate: requiredString,
});

const otherSchema = Yup.object().shape({
  ...baseSchema,
  name: requiredMin3NoEdgeSpaces,
  currencyUuid: requiredString,
});


export const validationSchemas: Record<string, Yup.ObjectSchema<any>> = {
  stocks: stocksSchema,
  shares: sharesSchema,
  real_estate: realEstateSchema,
  crypto: cryptoSchema,
  precious_stones: preciousStonesSchema,
  precious_metals: preciousMetalsSchema,
  treasury_bonds: treasuryBondsSchema,
  artwork: artworkSchema,
  investment_policy: investmentPolicySchema,
  loans: loansSchema,
  currencies: currenciesSchema,
  other: otherSchema,
};

export const getValidationSchema = (slug: string, isAdmin = false) => {
  const schema = validationSchemas[slug] || otherSchema;
  if (!isAdmin) return schema;
  return schema.shape({ status: Yup.string().required(VALIDATION_ENUM.REQUIRED) });
};
